/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICache } from '@msdyn365-commerce/cache-internal';
import msdyn365Commerce, {
    IDictionary,
    IFeatureVersions,
    IModule,
    IModuleContract,
    IPageResponse,
    IPlatformSettings,
    IRequestContext,
    IResourceManager,
    isAuthoringEditEnabled,
    isEmptyOrNullObject
} from '@msdyn365-commerce/core-internal';
import { AsyncResult, isAsyncResult } from '@msdyn365-commerce/retail-proxy';
import { EXCEPTION_BADPAGECONTEXT, EXCEPTION_INITIALDATAFAIL, ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { isObservableArray, toJS, transaction } from 'mobx';
import { IModuleBinder } from '../app-initialization/models';
import {
    CACHE_SETTING,
    MODULE_ACTION_KEY,
    MODULE_CACHE_KEY,
    MODULE_CONFIG_ERRORS_KEY,
    MODULE_CONFIG_KEY,
    MODULE_DATA_KEY,
    MODULE_DEFERRED_KEY,
    MODULE_RESOURCES_KEY,
    MODULE_VIEW_CACHE_KEY,
    MSDYN365_WYSIWYG_CANADDMODULE,
    MSDYN365_WYSIWYG_DISABLED_PROPS
} from '../consts';
import { getParentModuleName, isModuleEntryPointsEnabled } from '../utils/helpers';
import { getWrappedProxyObject } from './authoring-edit-helper';

/**
 * Interface containing module metadata that pageContext holds
 *
 * Any change to this interface should result an accompanying change to the moduleList
 * Fast JSON stringify constructor in base-page-utils.ts
 */
interface IModuleMetaData {
    id: string;
    typeName: string;
    dataActions: unknown;
}

interface IActionMetaData {
    name: string;
    entity: string;
    inputs: string[];
}

/**
 * Class to hold the page view model, areas and regions
 */
export class PageContext {
    public requestContext: IRequestContext;
    public pageRoot: IModuleContract & { theme: string };
    public requestCache: ICache;
    private _moduleList: IModuleMetaData[] = [];
    private _themeModulesList: string[] = [];
    private _clientPlatformSetting: IPlatformSettings = <IPlatformSettings>{};
    private _environmentVariable: IDictionary<string> = {};
    private _versionDependentFeatureInfo: IFeatureVersions = {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(pageData: IPageResponse | any, requestContext: IRequestContext, requestCache: ICache, resourceManager?: IResourceManager) {
        if (isEmptyOrNullObject(pageData)) {
            throw new Error(EXCEPTION_BADPAGECONTEXT);
        }
        this.requestContext = requestContext;
        this.requestCache = requestCache;
        this.pageRoot = pageData.pageRoot;
        this._versionDependentFeatureInfo = msdyn365Commerce.getVersionDependentFeatureInfo;

        if (typeof window === 'undefined') {
            this._putRequestedModulesInCacheOnServer(pageData, resourceManager);
        }

        this._initThemeModulesList();
        this.setEnvironmentVariableForCLient();
    }

    /**
     * Returns callback function that returns module rendering metadata for a given module
     */
    get modules(): (name: string) => IModule {
        return (name: string) => {
            const cacheItem = this.requestCache.getValue<IModule>({ typeName: MODULE_CACHE_KEY, key: name });
            if (!cacheItem) {
                throw new Error(`requested module id [${name}] is not found in module dictionary of pageconfig`);
            }

            const data = { ...(this.requestCache.getValue<IModule>({ typeName: MODULE_DATA_KEY, key: name }) || {}) };
            const actions = this.requestCache.getValue<IActionMetaData[]>({ typeName: MODULE_ACTION_KEY, key: name });
            const resources = this.requestCache.getValue<IDictionary<string>>({ typeName: MODULE_RESOURCES_KEY, key: cacheItem.typeName });

            // toJS method
            if (actions && toJS(actions)) {
                for (const action of toJS(actions)) {
                    if (action.entity && action.inputs) {
                        data[action.name] =
                            Array.isArray(action.inputs) || isObservableArray(action.inputs)
                                ? action.inputs.map((cacheKey: string) => this._populateDataActionResultFromCache(action.entity, cacheKey))
                                : this._populateDataActionResultFromCache(action.entity, action.inputs);
                    } else {
                        data[action.name] = AsyncResult.reject('Action Input Creation Failure');
                    }
                }
            }

            const config = this.requestCache.getValue<IDictionary<{}>>({ typeName: MODULE_CONFIG_KEY, key: name }) || {};
            return {
                ...cacheItem,
                data: data,
                config:
                    !(typeof window === 'undefined') && isAuthoringEditEnabled(this.requestContext)
                        ? getWrappedProxyObject(config, name, cacheItem.typeName)
                        : config,
                configErrors: this.requestCache.getValue({ typeName: MODULE_CONFIG_ERRORS_KEY, key: name }),
                resources: resources || {},
                disabledProperties: this.requestCache.getValue({ typeName: MSDYN365_WYSIWYG_DISABLED_PROPS, key: name }) || [],
                slotsForAddModule: this.requestCache.getValue({ typeName: MSDYN365_WYSIWYG_CANADDMODULE, key: name }) || []
            };
        };
    }

    /**
     * Get all the modules listed in the PageContext from the cache
     */
    get allModules(): IModule[] {
        return this._moduleList.map(moduleMetaData => {
            return this.modules(moduleMetaData.id);
        });
    }

    public setAppCacheEntries(appCacheEntries: string[]): void {
        this.requestCache.put({ typeName: CACHE_SETTING, key: 'APP_CACHE_ENTRIES' }, { item: appCacheEntries });
    }

    public setPlatformSettingsForClient(platformSettings: IPlatformSettings): void {
        this._clientPlatformSetting.clientSideDataActionTimeoutInMs = platformSettings?.clientSideDataActionTimeoutInMs || undefined;
        this._clientPlatformSetting.shouldUseInstanceCache = !!platformSettings?.clientSideDataActionTimeoutInMs;
        this._clientPlatformSetting.disableActionRegOptimization = true;
        this._clientPlatformSetting.imageFallbackOptimize = platformSettings?.imageFallbackOptimize || false;
        this._clientPlatformSetting.enableModuleEntryPoints = isModuleEntryPointsEnabled(platformSettings);
        /* if (platformSettings?.disableActionRegOptimization === true) {
            this._clientPlatformSetting.disableActionRegOptimization = true;
        }*/
    }

    public setEnvironmentVariableForCLient = () => {
        const environment = msdyn365Commerce.getAllEnvironmentVariable();
        this._environmentVariable = environment;
    };

    public getPlatformSettingsForClient(): IPlatformSettings {
        return this._clientPlatformSetting;
    }

    public getVersionDependentFeatureInfo(): IFeatureVersions {
        return this._versionDependentFeatureInfo;
    }

    /**
     * Checkis if the given module is deferred module
     * @param name name of the module
     */
    public deferred(name: string): Boolean {
        return !!this.requestCache.getValue({ typeName: MODULE_DEFERRED_KEY, key: name });
    }

    public getResourceTextForPlaceholderPreview(): string {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resourceItem = this.requestCache.get<any>({ typeName: MODULE_RESOURCES_KEY, key: 'PLACE-HOLDER-FOR-CONTAINER-PREVIEW' });
        if (resourceItem && resourceItem.item && resourceItem.item.clickToConfigure) {
            return resourceItem.item.clickToConfigure;
        } else {
            return 'Click here to configure';
        }
    }

    /**
     * Marks module as deferred
     * @param name name of the module
     */
    public markModuleAsDeferred(name: string): void {
        this.requestCache.put(
            { typeName: MODULE_DEFERRED_KEY, key: name },
            {
                item: true
            }
        );
    }

    /**
     * Inserts requested modules into cache on client side
     * @param pageData page data object
     * @param telemetry telemetry object
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async putRequestedModulesInCacheOnClient(pageData: any, telemetry: ITelemetry): Promise<void> {
        if (isEmptyOrNullObject(pageData.requestCache) || isEmptyOrNullObject(pageData.requestCache.internalCache)) {
            throw new Error(EXCEPTION_INITIALDATAFAIL);
        }

        for (const cacheType of Object.keys(pageData.requestCache.internalCache)) {
            const cacheBasedOnType = pageData.requestCache.internalCache[cacheType];
            if (!isEmptyOrNullObject(cacheBasedOnType)) {
                for (const cachekey of Object.keys(cacheBasedOnType)) {
                    const cacheItem = cacheBasedOnType[cachekey];

                    // AsyncResults sent from server to client get serialized into basic JS objects,
                    // They need to be "rehydrated" into actual AsyncResults to enable full functionality
                    if (cacheItem.item && cacheItem.item._type && cacheItem.item._type === 'AsyncResult') {
                        const metadata = cacheItem.item.metadata || {};
                        if (cacheItem.item.status && cacheItem.item.status === 'SUCCESS') {
                            cacheItem.item = AsyncResult.resolve(cacheItem.item.result);
                        } else {
                            cacheItem.item = AsyncResult.reject(cacheItem.item.error || 'Unable to parse data action');
                        }
                        cacheItem.item.metadata = metadata;
                    }

                    this.requestCache.put({ typeName: cacheType, key: cachekey }, { item: cacheItem.item });

                    // Awaiting a rejected promise will throw, so we need to catch those errors.
                    try {
                        await cacheItem.item;
                    } catch (e) {
                        telemetry.debug(`Error while hyrdating cache item, Type: ${cacheBasedOnType}, Key: ${cachekey}`);
                    }

                    if (cacheType === MODULE_CACHE_KEY) {
                        const moduleItem = <IModuleContract>cacheItem.item;
                        this._moduleList.push({
                            id: moduleItem.id,
                            typeName: moduleItem.typeName,
                            dataActions: moduleItem.dataActions || []
                        });
                    }
                }
            }
        }
    }

    /**
     * Populates data actions results from cache
     * @param cacheObjectType entity type
     * @param cacheKey cache key
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _populateDataActionResultFromCache(cacheObjectType: string, cacheKey: string): any {
        const cachedValueBasedOnInput = this.requestCache.getValue<object>({ typeName: cacheObjectType, key: cacheKey });
        if (!cachedValueBasedOnInput) {
            return;
        }

        // Ref resolution for promises needs to be delayed until the value is available
        if (isAsyncResult(cachedValueBasedOnInput)) {
            return transaction(() => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                cachedValueBasedOnInput.then(promiseResult => {
                    // Directly set the result because we want a synchronous change
                    cachedValueBasedOnInput.result = this._resolveCacheRef(cacheObjectType, <object>promiseResult);
                });
                return cachedValueBasedOnInput;
            });
        }

        return this._resolveCacheRef(cacheObjectType, cachedValueBasedOnInput);
    }

    /**
     * Resolves cache reference for the given entity type
     * @param cacheObjectType entity type that is cached
     * @param cacheRefInfo cache ref key
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _resolveCacheRef(cacheObjectType: string, cacheRefInfo: object): any {
        if (typeof cacheRefInfo !== 'object' || cacheRefInfo['$ref'] !== true) {
            return cacheRefInfo;
        }
        const result = cacheRefInfo['key']
            .map(key => {
                const value = this.requestCache.getValue({ typeName: cacheObjectType, key });
                if (value) {
                    return value;
                }
            })
            .filter(Boolean);
        return result && result.length > 0 ? (cacheRefInfo['isCollection'] ? result : result[0]) : null;
    }

    /**
     * Inserts requested modules in cache to prepare the initialData that will be sent to client
     * @param pageData pageData object with list of modules
     * @param resourceManager resourceManager that contains the localized strings
     */
    private _putRequestedModulesInCacheOnServer(pageData: IPageResponse, resourceManager?: IResourceManager): void {
        const isEditor = (this.requestContext.params && this.requestContext.params.isEditor) || false;
        const themeName = this.requestContext.params?.theme;
        for (let i = 0; i < pageData.slots.length; ++i) {
            const moduleInstance = pageData.slots[i];
            const moduleId = moduleInstance.id;
            this._moduleList.push({
                id: moduleInstance.id,
                typeName: moduleInstance.typeName,
                dataActions: moduleInstance.dataActions
            });

            this.requestCache.put(
                { typeName: MODULE_CACHE_KEY, key: moduleId },
                {
                    item: { id: moduleInstance.id, typeName: moduleInstance.typeName }
                }
            );
            this.requestCache.put({ typeName: MODULE_CONFIG_KEY, key: moduleId }, { item: moduleInstance.config });
            this.requestCache.put({ typeName: MODULE_CONFIG_ERRORS_KEY, key: moduleId }, { item: moduleInstance.configErrors });
            this.requestCache.put({ typeName: MODULE_DATA_KEY, key: moduleId }, { item: moduleInstance.data });
            this.requestCache.put({ typeName: MODULE_ACTION_KEY, key: moduleId }, { item: moduleInstance.dataActions });
            this.requestCache.put({ typeName: MSDYN365_WYSIWYG_DISABLED_PROPS, key: moduleId }, { item: [] });
            this.requestCache.put({ typeName: MSDYN365_WYSIWYG_CANADDMODULE, key: moduleId }, { item: [] });

            if (resourceManager && !isEditor) {
                this.requestCache.put(
                    { typeName: MODULE_RESOURCES_KEY, key: moduleInstance.typeName },
                    { item: resourceManager.getModuleResources(moduleInstance.typeName, themeName, this.requestContext.locale) }
                );
            }
        }
        // In editor mode, put the resources for all modules for the current locale into the cache
        if (isEditor && resourceManager && this.requestContext.features.enableAuthoringRemoveAndAddModule) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const bindings = (<any>msdyn365Commerce).getAllModuleBinder();
            bindings.forEach((moduleBinder: IModuleBinder) => {
                this.requestCache.put(
                    { typeName: MODULE_RESOURCES_KEY, key: moduleBinder.name },
                    { item: resourceManager.getModuleResources(moduleBinder.name, themeName, this.requestContext.locale) }
                );
            });
        }
    }

    /**
     * Initializes theme modules list that need to be loaded on the client
     */
    private _initThemeModulesList(): void {
        this._themeModulesList = [];

        if (this.requestContext.params.theme) {
            this._themeModulesList.push(this.requestContext.params.theme);
            this._themeModulesList.push(getParentModuleName(this.requestContext.params.theme));
        }
    }
}
